import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ResourceSearch from '../../components/ui/ResourceSearch';
import { blogService } from '../../services/blogService';

const SuppliersResource = () => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [pageSettings, setPageSettings] = useState({
    title: 'Suppliers Resource Center',
    subtitle: 'Strategic supplier partnership portal delivering comprehensive resources and tools for manufacturers, distributors, and B2B/B2C partners seeking to establish and optimize their presence within the 3D commerce ecosystem.',
    cta_title: 'Ready to Partner With Us?',
    cta_subtitle: 'Join our network of trusted suppliers and grow your business in the 3D commerce marketplace'
  });

  const getPageIcon = (page) => {
    switch (page) {
      case 'seller-toolbox': return 'Store';
      case 'suppliers-resource': return 'Truck';
      case 'documentation': return 'BookOpen';
      case 'api-reference': return 'Code';
      case 'tutorials': return 'Video';
      case 'resources-hub': return 'Globe';
      default: return 'FileText';
    }
  };

  useEffect(() => {
    const loadPageData = async () => {
      try {
        setLoading(true);
        const [posts, cats, settings] = await Promise.all([
          blogService.getPublishedPosts({ targetPage: 'suppliers-resource', limit: 12 }),
          blogService.getCategories(),
          blogService.getPageSettings('suppliers-resource')
        ]);
        setBlogPosts(posts);
        setCategories(cats || []);
        if (settings) setPageSettings(settings);
      } catch (error) {
        console.error('Error fetching suppliers resource data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPageData();
  }, []);

  const filteredPosts = activeCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category?.id === activeCategory);
  
  const resourceSections = [
  {
    id: 'partnerships',
    title: 'Partnership Program Overview',
    icon: 'Handshake',
    description: 'Collaboration opportunities, integration pathways, and mutual benefit structures',
    features: [
    {
      title: 'Partnership Tiers',
      items: [
      'Bronze: Entry-level suppliers (up to 100 products)',
      'Silver: Growing suppliers (100-500 products)',
      'Gold: Established suppliers (500-2000 products)',
      'Platinum: Enterprise suppliers (2000+ products)']

    },
    {
      title: 'Collaboration Benefits',
      items: [
      'Co-marketing opportunities and joint campaigns',
      'Priority listing in supplier directory',
      'Dedicated account manager support',
      'Early access to new platform features']

    },
    {
      title: 'Integration Pathways',
      items: [
      'API-first integration approach',
      'Custom integration consulting',
      'Third-party connector support',
      'Migration assistance from other platforms']

    }]

  },
  {
    id: 'integration',
    title: 'Technical Integration Hub',
    icon: 'Plug',
    description: 'API documentation, SDK resources, and implementation guides',
    features: [
    {
      title: 'API Documentation',
      items: [
      'RESTful API with OpenAPI specification',
      'Webhook support for real-time updates',
      'Rate limiting and authentication',
      'Sandbox environment for testing']

    },
    {
      title: 'SDK & Libraries',
      items: [
      'JavaScript/Node.js SDK',
      'Python integration library',
      'PHP connector package',
      'Custom SDK development support']

    },
    {
      title: 'Implementation Guides',
      items: [
      'Step-by-step integration tutorials',
      'Code samples and best practices',
      'Error handling and troubleshooting',
      'Performance optimization tips']

    }]

  },
  {
    id: 'bulk-ops',
    title: 'Bulk Operations Center',
    icon: 'Database',
    description: 'Mass product management, inventory sync, and catalog operations',
    features: [
    {
      title: 'Bulk Upload Tools',
      items: [
      'CSV/Excel bulk import (up to 10,000 products)',
      'Template generator for bulk data',
      'Validation and error reporting',
      'Automated image processing pipeline']

    },
    {
      title: 'Inventory Management',
      items: [
      'Real-time stock level synchronization',
      'Low stock alerts and notifications',
      'Multi-warehouse inventory tracking',
      'Automated reorder point calculations']

    },
    {
      title: 'Catalog Synchronization',
      items: [
      'Automated catalog updates (scheduled sync)',
      'Delta sync for changed products only',
      'Price and availability updates',
      'Product attribute mapping tools']

    }]

  },
  {
    id: 'supply-chain',
    title: 'Supply Chain Management',
    icon: 'Truck',
    description: 'Order fulfillment workflows, logistics coordination, and tracking systems',
    features: [
    {
      title: 'Order Fulfillment',
      items: [
      'Automated order routing to suppliers',
      'Multi-step fulfillment workflows',
      'Quality control checkpoints',
      'Packaging and shipping instructions']

    },
    {
      title: 'Logistics Coordination',
      items: [
      'Shipping carrier integration',
      'Real-time tracking and notifications',
      'International shipping support',
      'Return and exchange management']

    },
    {
      title: 'Performance Tracking',
      items: [
      'Order fulfillment metrics (speed, accuracy)',
      'Shipping performance analytics',
      'Customer satisfaction scores',
      'SLA compliance monitoring']

    }]

  }];


  const additionalResources = [
  {
    title: 'Business Intelligence Dashboard',
    icon: 'LineChart',
    description: 'Market insights, demand forecasting, and performance analytics',
    links: ['Market Trends Report', 'Demand Forecasting', 'Competitor Analysis', 'Revenue Projections']
  },
  {
    title: 'Compliance & Standards Center',
    icon: 'Shield',
    description: 'Platform policies, quality requirements, and regulatory compliance',
    links: ['Quality Standards', 'Platform Policies', 'Industry Regulations', 'Compliance Monitoring']
  },
  {
    title: 'Dedicated Account Management',
    icon: 'UserCheck',
    description: 'Direct communication channels and strategic consultation services',
    links: ['Contact Account Manager', 'Priority Support', 'Strategic Consultation', 'Quarterly Reviews']
  },
  {
    title: 'Training & Certification',
    icon: 'Award',
    description: 'Specialized education and certification programs for supplier teams',
    links: ['Platform Training', 'Integration Certification', 'Best Practices Course', 'Advanced Workshops']
  }];


  const contractResources = [
  { title: 'Partnership Agreement Template', icon: 'FileText' },
  { title: 'Terms & Conditions', icon: 'ScrollText' },
  { title: 'SLA (Service Level Agreement)', icon: 'ClipboardCheck' },
  { title: 'IP Protection Guidelines', icon: 'Lock' }];


  return (
    <div className="min-h-screen bg-slate-50 text-left">
      <Header />
      
      {/* Hero Section - DARK CONTRASTING BACKGROUND */}
      <div className="bg-slate-900 text-white py-24 border-b border-slate-800">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-600 rounded-2xl mb-6 shadow-lg shadow-green-500/20">
              <Icon name="Truck" size={40} className="text-white" />
            </div>
            <h1 className="text-5xl font-extrabold mb-6 tracking-tight">{pageSettings.title}</h1>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed font-medium">
              {pageSettings.subtitle}
            </p>
            <div className="flex justify-center gap-4 mb-12">
              <Button 
                variant="default" 
                className="bg-green-600 hover:bg-green-700 text-white border-none px-8 py-3"
                onClick={() => navigate('/supplier-signup')}
              >
                Become a Supplier
              </Button>
              <Button 
                variant="outline" 
                className="border-slate-700 text-white hover:bg-slate-800 px-8 py-3"
              >
                View Partnership Tiers
              </Button>
            </div>

            <ResourceSearch placeholder="Search supplier guides, API docs, logistics..." />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        
        {/* Blog / Articles Section */}
        <div className="mb-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
            <div>
              <h2 className="text-3xl font-bold text-slate-800">Supplier Resource Center</h2>
              <p className="text-slate-600 mt-2 text-lg">Insights, guides, and technical documentation for our supply partners</p>
            </div>
            <Link to="/resources-hub" className="text-blue-600 hover:text-blue-700 font-bold flex items-center group bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-100 transition-all hover:shadow-md">
              View All Articles
              <Icon name="ArrowRight" size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Category Filter Chips */}
          {!loading && categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${
                  activeCategory === 'all' 
                  ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                  : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                }`}
              >
                All Topics
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${
                    activeCategory === cat.id 
                    ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                    : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="grid md:grid-cols-3 gap-8 text-left">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-white rounded-2xl h-80 shadow-sm border border-slate-100" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {filteredPosts?.map((post) => (
                <Link 
                  key={post.id} 
                  to={`/${post.target_page || post.targetPage || 'blog'}/${post.slug}`}
                  className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-all group flex flex-col h-full"
                >
                  <div className="h-48 bg-slate-100 overflow-hidden relative">
                    {post.featured_image ? (
                      <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300">
                        <Icon name={getPageIcon(post.targetPage)} size={48} />
                      </div>
                    )}
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full uppercase tracking-wider shadow-sm">
                        {post.category?.name}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="mt-auto flex items-center text-blue-600 font-semibold text-sm">
                      Read More
                      <Icon name="ArrowRight" size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
              {filteredPosts?.length === 0 && (
                <div className="col-span-3 py-12 text-center text-slate-500 bg-white rounded-2xl border border-dashed border-slate-200">
                  No articles found in this category.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Core Resource Sections */}
        <div className="space-y-16">
          {resourceSections?.map((section, index) =>
          <div key={index} id={section?.id} className="scroll-mt-24">
              <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
                <div className="bg-gradient-to-r from-green-500 to-blue-600 p-8 text-white">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="p-3 bg-white/20 rounded-xl">
                      <Icon name={section?.icon} size={32} />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold">{section?.title}</h2>
                      <p className="text-green-100 mt-2">{section?.description}</p>
                    </div>
                  </div>
                </div>

                <div className="p-8">
                  <div className="grid md:grid-cols-3 gap-8">
                    {section?.features?.map((feature, idx) =>
                  <div key={idx} className="space-y-4">
                        <h3 className="text-xl font-bold text-slate-800 mb-4">{feature?.title}</h3>
                        <ul className="space-y-3">
                          {feature?.items?.map((item, itemIdx) =>
                      <li key={itemIdx} className="flex items-start space-x-3">
                              <Icon name="Check" size={20} className="text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-600">{item}</span>
                            </li>
                      )}
                        </ul>
                      </div>
                  )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Additional Resources */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">Additional Resources</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {additionalResources?.map((resource, index) =>
            <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="p-3 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg">
                    <Icon name={resource?.icon} className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{resource?.title}</h3>
                    <p className="text-slate-600 text-sm mb-4">{resource?.description}</p>
                    <div className="space-y-2">
                      {resource?.links?.map((link, linkIdx) =>
                    <a
                      key={linkIdx}
                      href="#"
                      className="flex items-center text-green-600 hover:text-green-700 text-sm group">

                          <Icon name="ArrowRight" size={16} className="mr-2 group-hover:translate-x-1 transition-transform" />
                          {link}
                        </a>
                    )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contract Resources */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">Contract & Legal Resources</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {contractResources?.map((resource, index) =>
            <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all text-center cursor-pointer group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                  <Icon name={resource?.icon} className="text-white" size={28} />
                </div>
                <h3 className="font-semibold text-slate-800 group-hover:text-green-600 transition-colors">
                  {resource?.title}
                </h3>
              </div>
            )}
          </div>
        </div>

        {/* Final CTA - DYNAMIC FROM DATABASE */}
        <div className="mt-20 text-center bg-white rounded-3xl p-12 shadow-xl border border-slate-100">
          <h2 className="text-4xl font-extrabold text-slate-800 mb-4">{pageSettings.cta_title}</h2>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            {pageSettings.cta_subtitle}
          </p>
          <Button 
            variant="default" 
            size="lg"
            className="bg-green-600 hover:bg-green-700 text-white font-bold px-10 py-4 rounded-xl shadow-lg shadow-green-600/20"
            onClick={() => navigate('/supplier-signup')}
          >
            Apply for Partnership
          </Button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SuppliersResource;
